import { getLanguage as getInterfaceLanguage } from '@/utils/i18n';
import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React, { useMemo } from 'react';
import { ChevronLeft, ChevronRight, Users, ExternalLink, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import type { UserCostEntry } from './userCostAnalysisService';

function formatBRL(value: number) {
  return value.toLocaleString(getInterfaceLanguage(), { style: 'currency', currency: 'BRL' });
}

function formatUSD(value: number) {
  return `USD ${value.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

function formatNumber(value: number) {
  return value.toLocaleString(getInterfaceLanguage());
}

interface UserCostTableProps {
  users: UserCostEntry[];
  loading: boolean;
  onUserClick: (userId: string) => void;
}

export function UserCostTable({ users, loading, onUserClick }: UserCostTableProps) {
  useLanguage();
  const [page, setPage] = React.useState(1);
  const [sortKey, setSortKey] = React.useState<'purchased_plan' | 'totalWithTaxBRL'>('totalWithTaxBRL');
  const [sortDirection, setSortDirection] = React.useState<'asc' | 'desc'>('desc');
  const perPage = 20;

  const totalPages = Math.ceil(users.length / perPage);

  const sortedUsers = useMemo(() => {
    const sorted = [...users].sort((a, b) => {
      if (sortKey === 'purchased_plan') {
        const planA = (a.purchased_plan || '').toLowerCase();
        const planB = (b.purchased_plan || '').toLowerCase();
        return sortDirection === 'asc' ? planA.localeCompare(planB) : planB.localeCompare(planA);
      }
      const valA = a[sortKey];
      const valB = b[sortKey];
      return sortDirection === 'asc' ? valA - valB : valB - valA;
    });
    return sorted;
  }, [users, sortKey, sortDirection]);

  const paginatedUsers = useMemo(() => {
    const start = (page - 1) * perPage;
    return sortedUsers.slice(start, start + perPage);
  }, [sortedUsers, page]);

  const handleSort = (key: 'purchased_plan' | 'totalWithTaxBRL') => {
    if (sortKey === key) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortKey(key);
      setSortDirection('desc');
    }
    setPage(1);
  };

  const SortIcon = ({ column }: { column: 'purchased_plan' | 'totalWithTaxBRL' }) => {
  useLanguage();
    if (sortKey !== column) return <ArrowUpDown size={10} className="text-slate-300" />;
    return sortDirection === 'asc' ? <ArrowUp size={10} className="text-blue-500" /> : <ArrowDown size={10} className="text-blue-500" />;
  };

  React.useEffect(() => {
    setPage(1);
  }, [users]);

  if (loading) {
    return (
      <div className="bg-white border border-slate-100 rounded-2xl shadow-sm overflow-hidden">
        <div className="p-12 text-center">
          <div className="inline-block w-6 h-6 border-2 border-blue-500 border-t-transparent rounded-full animate-spin" />
          <p className="text-xs text-slate-400 mt-3">{translateInterfaceText("Carregando análise de custos...")}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white border border-slate-100 rounded-2xl shadow-sm overflow-hidden">
      <div className="p-5 border-b border-slate-50 flex items-center gap-2">
        <Users size={14} className="text-blue-600" />
        <span className="text-[11px] text-slate-400 font-bold uppercase tracking-wider">{translateInterfaceText("Custos por Usuário")}</span>
        <span className="ml-auto text-[11px] text-slate-400 font-medium">{users.length} {" "}{translateInterfaceText("registro(s)")}</span>
      </div>

      {users.length === 0 ? (
        <div className="p-12 text-center space-y-2">
          <Users size={20} className="text-slate-300 mx-auto" />
          <p className="text-sm font-bold text-slate-800">{translateInterfaceText("Nenhum dado encontrado")}</p>
          <p className="text-xs text-slate-400">{translateInterfaceText("Ajuste o período e tente novamente.")}</p>
        </div>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-slate-50/50">
                  <th className="text-left px-4 py-2.5 text-[10px] font-bold text-slate-400 uppercase tracking-wider">{translateInterfaceText("Usuário")}</th>
                  <th className="text-left px-4 py-2.5 text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                    <button onClick={() => handleSort('purchased_plan')} className="flex items-center gap-1 hover:text-slate-600 transition-colors">
                      {translateInterfaceText("Plano")}{" "}<SortIcon column="purchased_plan" />
                    </button>
                  </th>
                  <th className="text-right px-4 py-2.5 text-[10px] font-bold text-slate-400 uppercase tracking-wider">{translateInterfaceText("Networking")}</th>
                  <th className="text-right px-4 py-2.5 text-[10px] font-bold text-slate-400 uppercase tracking-wider">{translateInterfaceText("Compute")}</th>
                  <th className="text-right px-4 py-2.5 text-[10px] font-bold text-slate-400 uppercase tracking-wider">{translateInterfaceText("Storage")}</th>
                  <th className="text-right px-4 py-2.5 text-[10px] font-bold text-slate-400 uppercase tracking-wider">{translateInterfaceText("Other")}</th>
                  <th className="text-right px-4 py-2.5 text-[10px] font-bold text-slate-400 uppercase tracking-wider">{translateInterfaceText("Total USD")}</th>
                  <th className="text-right px-4 py-2.5 text-[10px] font-bold text-slate-400 uppercase tracking-wider">{translateInterfaceText("Total BRL")}</th>
                  <th className="text-right px-4 py-2.5 text-[10px] font-bold text-slate-400 uppercase tracking-wider">{translateInterfaceText("Impostos")}</th>
                  <th className="text-right px-4 py-2.5 text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                    <button onClick={() => handleSort('totalWithTaxBRL')} className="flex items-center gap-1 justify-end hover:text-slate-600 transition-colors">
                      {translateInterfaceText("Total c/ Taxo")}{" "}<SortIcon column="totalWithTaxBRL" />
                    </button>
                  </th>
                  <th className="text-right px-4 py-2.5 text-[10px] font-bold text-slate-400 uppercase tracking-wider">{translateInterfaceText("Plays")}</th>
                  <th className="text-right px-4 py-2.5 text-[10px] font-bold text-slate-400 uppercase tracking-wider">{translateInterfaceText("Retenção")}</th>
                  <th className="text-center px-4 py-2.5 text-[10px] font-bold text-slate-400 uppercase tracking-wider w-20">{translateInterfaceText("Ação")}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {paginatedUsers.map((user) => (
                  <tr key={user.id_user} className="hover:bg-slate-50/50 transition-colors">
                    <td className="px-4 py-3 font-medium text-slate-700 max-w-[180px]">
                      {user.nome ? (
                        <div className="truncate" title={`${user.nome} ${user.sobrenome || ''} — ${user.email || user.id_user}`}>
                          <span className="block">{user.nome} {user.sobrenome || ''}</span>
                          <span className="block text-[10px] text-slate-400 truncate">{user.email || user.id_user}</span>
                        </div>
                      ) : (
                        <span className="truncate block" title={user.id_user}>
                          {user.id_user.length > 14 ? user.id_user.substring(0, 14) + '...' : user.id_user}
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-left">
                      {user.purchased_plan ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-blue-50 text-blue-600 border border-blue-100">
                          {user.purchased_plan}
                        </span>
                      ) : (
                        <span className="text-[10px] text-slate-300">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right text-orange-600 tabular-nums">{formatBRL(user.costBreakdown.networking.brl)}</td>
                    <td className="px-4 py-3 text-right text-blue-600 tabular-nums">{formatBRL(user.costBreakdown.compute.brl)}</td>
                    <td className="px-4 py-3 text-right text-emerald-600 tabular-nums">{formatBRL(user.costBreakdown.storage.brl)}</td>
                    <td className="px-4 py-3 text-right text-slate-500 tabular-nums">{formatBRL(user.costBreakdown.other.brl)}</td>
                    <td className="px-4 py-3 text-right text-slate-600 tabular-nums">{formatUSD(user.totalCostUSD)}</td>
                    <td className="px-4 py-3 text-right font-medium text-slate-700 tabular-nums">{formatBRL(user.totalCostBRL)}</td>
                    <td className="px-4 py-3 text-right text-amber-600 tabular-nums">{formatBRL(user.estimatedTaxBRL)}</td>
                    <td className="px-4 py-3 text-right font-bold text-slate-900 tabular-nums">{formatBRL(user.totalWithTaxBRL)}</td>
                    <td className="px-4 py-3 text-right text-slate-500 tabular-nums">{formatNumber(user.metrics.totalPlays)}</td>
                    <td className="px-4 py-3 text-right text-slate-500 tabular-nums">{formatNumber(user.metrics.totalRetention)}s</td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-center">
                        <button
                          onClick={() => onUserClick(user.id_user)}
                          className="p-1.5 rounded-lg hover:bg-blue-50 text-slate-400 hover:text-blue-600 transition-colors"
                          title={translateInterfaceText("Ver detalhes")}
                        >
                          <ExternalLink size={13} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {totalPages > 1 && (
            <div className="p-4 border-t border-slate-50 flex items-center justify-between">
              <span className="text-[11px] text-slate-400">
                {translateInterfaceText("Página")}{" "}{page} {" "}{translateInterfaceText("de")}{" "}{totalPages}
              </span>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => setPage(page - 1)}
                  disabled={page <= 1}
                  className="p-1.5 rounded-lg border border-slate-200 text-slate-400 hover:bg-slate-50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronLeft size={14} />
                </button>
                <button
                  onClick={() => setPage(page + 1)}
                  disabled={page >= totalPages}
                  className="p-1.5 rounded-lg border border-slate-200 text-slate-400 hover:bg-slate-50 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
                >
                  <ChevronRight size={14} />
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default UserCostTable;
